/*
 * Evans Brothers product gallery.
 *
 * Registers the custom element `media-gallery`, replacing Dawn's
 * assets/media-gallery.js on the main product section. It keeps the same public
 * contract, because assets/product-info.js drives the gallery on variant change
 * and expects:
 *
 *   - an element named `media-gallery`
 *   - a <ul> inside it whose children are li[data-media-id]
 *   - a setActiveMedia(mediaId, prepend) method
 *
 * Everything else - transitions, thumbnails, keyboard, counter, zoom - is ours.
 *
 * Only one definition of `media-gallery` can win per page. main-product.liquid
 * loads this instead of media-gallery.js; other sections that still use Dawn's
 * gallery load Dawn's, and the two never render together.
 */
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class EbMediaGallery extends HTMLElement {
      connectedCallback() {
        this.slidesList = this.querySelector('.eb-gallery__slides');
        if (!this.slidesList) return;

        this.thumbs = this.querySelector('.eb-gallery__thumbs');
        this.counter = this.querySelector('[data-counter-current]');
        this.liveRegion = this.querySelector('[data-live-region]');
        this.zoomButton = this.querySelector('.eb-gallery__zoom');
        this.lightbox = this.querySelector('[data-lightbox]');
        this.lightboxImage = this.querySelector('[data-lightbox-image]');
        this.lightboxCounter = this.querySelector('[data-lightbox-current]');

        this.#bind();
        this.#watchSlides();
        this.#syncFromDom();
      }

      /* ── Public contract ───────────────────────────────────────────── */

      /**
       * Show the media with this id.
       * @param {string} mediaId - "{sectionId}-{mediaId}".
       * @param {boolean} [prepend] - Move it to the front, as product-info does
       *   on variant change so the variant's image leads the gallery.
       */
      setActiveMedia(mediaId, prepend) {
        const slide = this.#slide(mediaId) || this.#slides()[0];
        if (!slide) return;

        if (prepend && slide.parentElement.firstElementChild !== slide) {
          slide.parentElement.prepend(slide);
          const thumbItem = this.#thumb(mediaId)?.closest('.eb-gallery__thumb-item');
          if (thumbItem && thumbItem.parentElement.firstElementChild !== thumbItem) {
            thumbItem.parentElement.prepend(thumbItem);
          }
        }

        this.#activate(slide);
      }

      /* ── Internals ─────────────────────────────────────────────────── */

      #slides() {
        return Array.from(this.slidesList.querySelectorAll('[data-media-id]'));
      }

      #slide(mediaId) {
        return this.slidesList.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
      }

      #thumb(mediaId) {
        return this.thumbs?.querySelector(`[data-target="${CSS.escape(mediaId)}"]`) ?? null;
      }

      #activeSlide() {
        return this.slidesList.querySelector('.is-active') || this.#slides()[0];
      }

      #bind() {
        // Thumbnails are delegated, because product-info.js replaces these
        // elements wholesale on variant change - listeners bound to individual
        // buttons would be lost.
        this.thumbs?.addEventListener('click', (event) => {
          const button = event.target.closest('[data-target]');
          if (!button) return;
          this.setActiveMedia(button.dataset.target, false);
        });

        this.querySelectorAll('[data-direction]').forEach((button) => {
          button.addEventListener('click', () => this.#step(button.dataset.direction === 'next' ? 1 : -1));
        });

        this.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          // Leave the arrows alone while a text field has focus.
          if (event.target.matches('input, textarea, select')) return;
          event.preventDefault();
          this.#step(event.key === 'ArrowRight' ? 1 : -1);
        });

        this.zoomButton?.addEventListener('click', () => this.#openZoom());

        // Clicking the image itself is the obvious way to zoom.
        this.slidesList.addEventListener('click', (event) => {
          if (!this.lightbox) return;
          if (!event.target.closest('.eb-gallery__image')) return;
          this.#openZoom();
        });

        if (!this.lightbox) return;

        this.lightbox.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.lightbox.close());

        this.lightbox.querySelectorAll('[data-lightbox-direction]').forEach((button) => {
          button.addEventListener('click', () => {
            this.#lightboxStep(button.dataset.lightboxDirection === 'next' ? 1 : -1);
          });
        });

        // Clicking the backdrop closes. The dialog element itself is the
        // backdrop, so a click landing on it - rather than on the inner panel -
        // means outside.
        this.lightbox.addEventListener('click', (event) => {
          if (event.target === this.lightbox) this.lightbox.close();
        });

        this.lightbox.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.#lightboxStep(event.key === 'ArrowRight' ? 1 : -1);
        });

        // Keep the page behind from scrolling while it is open.
        this.lightbox.addEventListener('close', () => {
          document.body.classList.remove('overflow-hidden');
          this.zoomButton?.focus();
        });
      }

      /** Move the lightbox on by one, wrapping at either end. */
      #lightboxStep(delta) {
        const slides = this.#slides();
        if (slides.length < 2) return;
        const current = slides.findIndex((slide) => slide.dataset.mediaId === this.lightboxMediaId);
        const next = slides[(current + delta + slides.length) % slides.length];
        this.#showInLightbox(next);
        // Keep the page gallery on the same image, so closing is not jarring.
        this.setActiveMedia(next.dataset.mediaId, false);
      }

      #showInLightbox(slide) {
        if (!this.lightboxImage) return;
        this.lightboxMediaId = slide.dataset.mediaId;
        this.lightboxImage.src = slide.dataset.full || slide.querySelector('img')?.src || '';
        this.lightboxImage.alt = slide.dataset.alt || '';
        if (this.lightboxCounter) {
          this.lightboxCounter.textContent = String(this.#slides().indexOf(slide) + 1);
        }
      }

      #step(delta) {
        const slides = this.#slides();
        if (slides.length < 2) return;
        const index = slides.indexOf(this.#activeSlide());
        // Wrap, so the arrows never dead-end.
        const next = slides[(index + delta + slides.length) % slides.length];
        this.setActiveMedia(next.dataset.mediaId, false);
      }

      #activate(slide) {
        const slides = this.#slides();

        slides.forEach((element) => {
          const active = element === slide;
          element.classList.toggle('is-active', active);
          // Keep inactive slides out of the tab order and off screen readers.
          element.toggleAttribute('inert', !active);
        });

        this.thumbs?.querySelectorAll('[data-target]').forEach((button) => {
          const active = button.dataset.target === slide.dataset.mediaId;
          button.classList.toggle('is-active', active);
          if (active) {
            button.setAttribute('aria-current', 'true');
            button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          } else {
            button.removeAttribute('aria-current');
          }
        });

        const position = slides.indexOf(slide) + 1;
        if (this.counter) this.counter.textContent = String(position);
        if (this.liveRegion) this.liveRegion.textContent = `Image ${position} of ${slides.length}`;

        // The zoom modal reads data-media-id off whatever opened it.
        this.zoomButton?.setAttribute('data-media-id', slide.dataset.mediaId);

        this.#playActive(slide);
      }

      /** Pause any video or model that is no longer showing. */
      #playActive(slide) {
        this.#slides().forEach((element) => {
          if (element === slide) return;
          element.querySelector('video')?.pause();
          const deferred = element.querySelector('deferred-media');
          if (deferred?.getAttribute('loaded')) {
            deferred.querySelector('video, iframe, model-viewer')?.pause?.();
          }
        });
      }

      /**
       * Open on the image the shopper is looking at. Dawn's modal opened on
       * whichever media its own markup listed first, which is why it so often
       * showed the wrong one.
       */
      #openZoom() {
        if (!this.lightbox) return;
        const active = this.#activeSlide();
        if (!active) return;

        // Non-image media has no meaningful full-size still to enlarge.
        if (active.dataset.mediaType && active.dataset.mediaType !== 'image') return;

        this.#showInLightbox(active);
        document.body.classList.add('overflow-hidden');
        if (typeof this.lightbox.showModal === 'function') {
          this.lightbox.showModal();
        } else {
          this.lightbox.setAttribute('open', '');
        }
      }

      /**
       * product-info.js rewrites the slide list on variant change - reordering,
       * adding and removing li[data-media-id] - and knows nothing about our
       * thumbnails. Rebuild them from the slides whenever that happens, so the
       * two can never drift apart.
       */
      #watchSlides() {
        if (!this.thumbs) return;

        this.slideObserver = new MutationObserver(() => this.#rebuildThumbs());
        this.slideObserver.observe(this.slidesList, { childList: true });
      }

      #rebuildThumbs() {
        const slides = this.#slides();
        const existing = new Map(
          Array.from(this.thumbs.querySelectorAll("[data-target]")).map((button) => [
            button.dataset.target,
            button.closest(".eb-gallery__thumb-item"),
          ])
        );

        const ordered = slides.map((slide, index) => {
          const id = slide.dataset.mediaId;
          // Reuse the existing thumb where we have one, so images are not refetched.
          const reused = existing.get(id);
          if (reused) {
            existing.delete(id);
            return reused;
          }

          const item = document.createElement("li");
          item.className = "eb-gallery__thumb-item";
          const button = document.createElement("button");
          button.type = "button";
          button.className = "eb-gallery__thumb";
          button.dataset.target = id;
          button.setAttribute("aria-label", `Show image ${index + 1}`);
          const img = document.createElement("img");
          img.src = slide.dataset.thumb || "";
          img.alt = "";
          img.loading = "lazy";
          button.appendChild(img);
          item.appendChild(button);
          return item;
        });

        // Drop thumbs whose slide has gone.
        existing.forEach((item) => item?.remove());
        ordered.forEach((item) => this.thumbs.appendChild(item));

        const active = this.#activeSlide();
        if (active) this.#activate(active);
      }

      disconnectedCallback() {
        this.slideObserver?.disconnect();
      }

      /** Adopt whatever is marked active in the server-rendered markup. */
      #syncFromDom() {
        const active = this.#activeSlide();
        if (active) this.#activate(active);
      }
    }
  );
}
